/**
 * Modern TanStack Data Table - Advanced Data Grid with Best-in-Class UX
 *
 * ⚠️ APPROVED ABSTRACTION - PRIMARY STANDARD FOR ALL TABLES
 * This is the standard table component for all main list pages in the application.
 * See docs/TABLE_STANDARDS.md for complete guidelines.
 *
 * A comprehensive data table with enterprise-grade features:
 * - Smart search with operators
 * - Quick filter pills for common filters
 * - Advanced filter builder with operators
 * - Active filter display with pills
 * - Table settings menu for column visibility
 * - Column reordering with drag and drop (opt-in)
 * - Column sorting with up/down arrows
 * - Row selection with checkboxes
 * - Row action menus (coming soon)
 * - Hover states and smooth transitions
 * - Modern pagination
 * - Full keyboard navigation
 * - Complete accessibility support
 *
 * Usage Pattern:
 * ```tsx
 * // 1. Use with useTanStackTable hook
 * const { table, isLoading, error, totalCount, setItemsPerPage, setCurrentPage } =
 *   useTanStackTable({
 *     columns,
 *     queryHook: useEntityQueryAdapter,
 *     initialSort: [{ id: 'created_at', desc: true }],
 *     storageKey: 'entity-table',
 *   });
 *
 * // 2. Render with ModernTanStackDataTable
 * <ModernTanStackDataTable
 *   table={table}
 *   isLoading={isLoading}
 *   error={error}
 *   searchConfig={{ enabled: true, placeholder: "Search items..." }}
 *   quickFilters={quickFilters}
 *   filterConfig={{ enabled: true, filters: [...] }}
 *   enableTableSettings={true}
 *   enableColumnReordering={true}
 *   columnOrderStorageKey="my-table"
 *   onItemsPerPageChangeDirect={setItemsPerPage}
 *   onPageChangeDirect={setCurrentPage}
 * />
 * ```
 *
 * Reference Implementations:
 * - app/(main)/samples/page.tsx (full features)
 * - app/(main)/team/page.tsx (with navigation tabs)
 * - app/(main)/locations/page.tsx (standard)
 *
 * @see {@link docs/TABLE_STANDARDS.md} for complete guidelines
 * @see {@link lib/hooks/tables/README.md} for technical documentation
 */

"use client";

import { EmptyState } from "@/components/ui/primitives/data-display";
import type { EmptyStateVariant } from "@/components/ui/primitives/empty-state";
import {
  DateRangeDropdown,
  type DateRangeValue,
} from "@/components/ui/primitives/date-range-filter";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/primitives/searchable-select";
import {
  InlineFilters,
  type InlineFilterConfig,
} from "@/components/ui/primitives/inline-filters";
import { ModernPagination } from "@/components/ui/primitives/modern-pagination";
import { type RowAction } from "@/components/ui/primitives/row-actions-menu";
import { TableCheckbox } from "@/components/ui/primitives/table-checkbox";
import { type TableColumn } from "@/components/ui/primitives/table-settings-menu";
import {
  EnhancedTableToolbar,
  type AvailableFilter,
} from "@/components/ui/primitives/table-toolbar";
import { useFilterVisibility } from "@/lib/hooks/tables/useFilterVisibility";
import {
  applyColumnSortDirection,
  applyMovableOrder,
  toSelectorColumn,
} from "@/lib/utils/column-order";
import { getConsentedStorage } from "@/lib/utils/consented-storage";
import { normalizeDateRangeValue } from "@/lib/utils/filter-utils";
import { appliedFilterIdsOf } from "@/lib/utils/filter-visibility";
import { cn } from "@/lib/utils/general-utils";
import {
  cellContentNeedsMinWidthZero,
  getCellTextClass,
} from "@/lib/utils/table-styles";
import type { ColumnMetaConfig } from "@/types/central/ui";
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
} from "lucide-react";
import {
  flexRender,
  type ReactTable as TanStackTable,
  type RowData,
  type StockFeatures,
} from "@tanstack/react-table";
import * as React from "react";

const { useCallback, useEffect, useMemo, useState } = React;

// =============================================================================
// STABLE DEFAULT VALUES
// =============================================================================
// Default prop values must be defined at module level to maintain referential
// stability across renders. Inline defaults like `filterValues = {}` create
// new object references every render, causing useMemo/useCallback hooks that
// depend on them to recalculate unnecessarily and potentially trigger re-render loops.
const DEFAULT_FILTER_VALUES: Record<
  string,
  string | string[] | { from: string; to: string }
> = {};
const DEFAULT_COLUMN_CONFIG: Record<
  string,
  { canHide?: boolean; canReorder?: boolean }
> = {};
const DEFAULT_PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100];
const DEFAULT_RECENT_SEARCHES: string[] = [];
const DEFAULT_BULK_ACTIONS: BulkAction[] = [];
const EMPTY_SELECTED_ROWS: never[] = [];

// =============================================================================
// TYPES
// =============================================================================

export interface SearchConfig {
  enabled: boolean;
  placeholder?: string;
  /** Searchable fields for smart search operators */
  fields?: Array<{ id: string; label: string }>;
}

export interface FilterConfig {
  enabled: boolean;
  filters: InlineFilterConfig[];
}

export interface BulkAction {
  /** Unique identifier for the action */
  id: string;
  /** Display label */
  label: string;
  /** Icon component (optional) */
  icon?: React.ReactNode;
  /** Handler function - receives array of selected row data */
  onClick: () => void;
  /** Variant for styling */
  variant?: "default" | "danger";
  /** Whether the action is currently loading */
  isLoading?: boolean;
  /** Whether the action is disabled */
  disabled?: boolean;
}

export interface ModernTanStackDataTableProps<TData extends RowData> {
  /** TanStack Table instance */
  table: TanStackTable<StockFeatures, TData>;

  /** Loading state (true only on initial load with no cached data) */
  isLoading?: boolean;

  /** Fetching state (true when refetching, e.g., during pagination/filter changes) */
  isFetching?: boolean;

  /** Error state */
  error?: Error | null;

  /** Search configuration */
  searchConfig?: SearchConfig;

  /**
   * @deprecated Quick filters have been replaced by SearchableSelect components.
   * Convert quick filter options into filterConfig select filters instead.
   */
  quickFilters?: unknown[];

  /**
   * @deprecated Use filterConfig select filters instead.
   */
  activeQuickFilter?: string;

  /**
   * @deprecated Use filterConfig select filters instead.
   */
  onQuickFilterChange?: (_filterId: string) => void;

  /** Filter configuration */
  filterConfig?: FilterConfig;

  /** Empty state message */
  emptyMessage?: string;

  /** EmptyState variant for icon-based empty state */
  emptyVariant?: EmptyStateVariant;

  /** Row click handler */
  onRowClick?: (_rowData: TData) => void;

  /** Row className generator */
  rowClassName?: (_rowData: TData) => string;

  /** Table container className */
  className?: string;

  /** Total count of items (for pagination display) */
  totalCount?: number;

  /** External search state handler */
  onSearchChange?: (_searchTerm: string) => void;

  /** External filter state handler */
  onFiltersChange?: (
    _filterValues: Record<
      string,
      string | string[] | { from: string; to: string }
    >,
  ) => void;

  /** Current search value */
  searchValue?: string;

  /** Current filter values */
  filterValues?: Record<
    string,
    string | string[] | { from: string; to: string }
  >;

  /** Recent searches for smart search */
  recentSearches?: string[];

  /** Clear recent searches handler */
  onClearRecentSearches?: () => void;

  /** Enable row selection with checkboxes */
  enableRowSelection?: boolean;

  /** Callback when row selection changes */
  onRowSelectionChange?: (_selectedRows: TData[]) => void;

  /** Bulk actions configuration */
  bulkActions?: BulkAction[];

  /** Row actions generator - NOT YET IMPLEMENTED */
  rowActions?: (_rowData: TData) => RowAction[];

  /** Toolbar custom actions */
  toolbarActions?: React.ReactNode;

  /** Enable table settings menu for column visibility */
  enableTableSettings?: boolean;

  /** Configuration for columns that cannot be hidden */
  columnConfig?: {
    [columnId: string]: {
      canHide?: boolean;
      canReorder?: boolean;
    };
  };

  /**
   * Enable column reordering via drag and drop.
   * Defaults to false (opt-in), unlike DataTableToolbar which defaults true
   * only when the table has a controlled columnOrder.
   */
  enableColumnReordering?: boolean;

  /** Storage key for persisting column order */
  columnOrderStorageKey?: string;

  /** Direct items per page setter from useTanStackTable hook */
  onItemsPerPageChangeDirect?: (_itemsPerPage: number) => void;

  /** Direct page change setter from useTanStackTable hook */
  onPageChangeDirect?: (_page: number) => void;

  /** Page size options for the pagination selector */
  pageSizeOptions?: number[];

  /** Whether to show the filter selector (funnel icon toggle). When false, all filters remain visible but the selector is hidden. */
  showFilterSelector?: boolean;

  /** Current View Mode (Table vs Grid) */
  viewMode?: "table" | "grid";

  /** Render function for Grid View */
  renderGrid?: (_items: TData[]) => React.ReactNode;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ModernTanStackDataTable<TData extends RowData>({
  table,
  isLoading = false,
  isFetching = false,
  error = null,
  searchConfig,
  quickFilters: _quickFilters,
  activeQuickFilter: _activeQuickFilter,
  onQuickFilterChange: _onQuickFilterChange,
  filterConfig,
  emptyMessage = "No data available",
  emptyVariant,
  onRowClick,
  rowClassName,
  className = "",
  totalCount = 0,
  onSearchChange,
  onFiltersChange,
  searchValue = "",
  filterValues = DEFAULT_FILTER_VALUES,
  recentSearches = DEFAULT_RECENT_SEARCHES,
  onClearRecentSearches,
  enableRowSelection = false,
  onRowSelectionChange,
  bulkActions = DEFAULT_BULK_ACTIONS,
  rowActions: _rowActions,
  toolbarActions,
  enableTableSettings = true,
  columnConfig = DEFAULT_COLUMN_CONFIG,
  enableColumnReordering = false,
  columnOrderStorageKey,
  onItemsPerPageChangeDirect,
  onPageChangeDirect,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showFilterSelector = true,
  viewMode = "table",
  renderGrid,
}: Readonly<ModernTanStackDataTableProps<TData>>) {
  const {
    enabledFilterIds,
    handleToggleFilterVisibility,
    handleSetAllFilterVisibility,
  } = useFilterVisibility(filterConfig?.filters, filterValues, onFiltersChange);

  // State for column dragging
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // Get all leaf columns
  const allColumns = table.getAllLeafColumns();

  // Store the default column order on first render using a ref
  // This captures the original order from column definitions before any reordering
  const defaultColumnOrderRef = React.useRef<string[]>([]);
  if (defaultColumnOrderRef.current.length === 0) {
    defaultColumnOrderRef.current = allColumns.map((col) => col.id);
  }

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (enableColumnReordering && columnOrderStorageKey) {
      const stored = getConsentedStorage().getItem(
        `column-order-${columnOrderStorageKey}`,
      );
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return allColumns.map((col) => col.id);
        }
      }
    }
    return allColumns.map((col) => col.id);
  });

  // Update table column order
  useEffect(() => {
    if (enableColumnReordering) {
      table.setColumnOrder(columnOrder);
    }
  }, [columnOrder, enableColumnReordering, table]);

  useEffect(() => {
    if (enableColumnReordering && columnOrderStorageKey) {
      getConsentedStorage().setItem(
        `column-order-${columnOrderStorageKey}`,
        JSON.stringify(columnOrder),
      );
    }
  }, [columnOrder, enableColumnReordering, columnOrderStorageKey]);

  // Get visible leaf columns for rendering (in correct order)
  const columns = table.getVisibleLeafColumns();

  // Container ref for measuring actual table width
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(1200);

  // Measure container width on mount and resize
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        setContainerWidth(width);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Default max width per column (prevents over-stretching)
  const DEFAULT_MAX_WIDTH = 400;

  // Calculate intelligent column widths based on visible columns and available space
  // When few columns are visible, constrain widths to prevent over-stretching
  const columnWidths = useMemo(() => {
    const visibleColumns = table.getVisibleLeafColumns();

    // Calculate base sizes with max constraints from column meta
    const columnSizes = visibleColumns.map((col) => {
      const baseSize = col.getSize();
      const meta = col.columnDef.meta as ColumnMetaConfig | undefined;
      const maxSize = meta?.maxSize ?? DEFAULT_MAX_WIDTH;
      return {
        id: col.id,
        baseSize,
        maxSize,
      };
    });

    const totalBaseSize = columnSizes.reduce((sum, c) => sum + c.baseSize, 0);
    const visibleColumnCount = visibleColumns.length;

    // Determine if we should constrain columns (few columns = use fixed widths)
    // Use container width to determine if proportional would stretch too much
    const shouldConstrain =
      visibleColumnCount <= 4 || totalBaseSize < containerWidth * 0.5;

    return columnSizes.reduce(
      (acc, col) => {
        if (shouldConstrain) {
          // Few columns - use actual pixel widths with reasonable multiplier, capped at maxSize
          // Apply 1.3x multiplier to give columns some breathing room but not stretch infinitely
          const calculatedWidth = Math.min(col.baseSize * 1.3, col.maxSize);
          acc[col.id] = `${calculatedWidth}px`;
        } else {
          // Normal case - use percentage but cap at maxSize using CSS min()
          const percentage = (col.baseSize / totalBaseSize) * 100;
          acc[col.id] = `min(${percentage}%, ${col.maxSize}px)`;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, table.state.columnVisibility, containerWidth]);

  const minTableWidth = useMemo(() => {
    const visibleColumns = table.getVisibleLeafColumns();
    return visibleColumns.reduce((sum, col) => sum + col.getSize(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, table.state.columnVisibility]);

  // Build table columns for settings menu
  const tableColumns = useMemo<TableColumn[]>(() => {
    const firstLeafId = allColumns[0]?.id;
    return allColumns.map((column) =>
      toSelectorColumn(column, firstLeafId, columnConfig[column.id]),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allColumns,
    columnConfig,
    table.state.columnVisibility,
    table.state.columnOrder,
    table.state.sorting,
  ]);

  // Handle column visibility change
  const handleColumnVisibilityChange = useCallback(
    (columnId: string, visible: boolean) => {
      const firstLeafId = table.getAllLeafColumns()[0]?.id;
      const column = table.getColumn(columnId);
      if (
        !visible &&
        (columnId === firstLeafId || (column ? !column.getCanHide() : false))
      ) {
        return;
      }
      if (column) {
        column.toggleVisibility(visible);
      }
    },
    [table],
  );

  const handleResetColumnOrder = useCallback(() => {
    setColumnOrder(defaultColumnOrderRef.current);
    if (enableColumnReordering && columnOrderStorageKey) {
      getConsentedStorage().removeItem(`column-order-${columnOrderStorageKey}`);
    }
  }, [enableColumnReordering, columnOrderStorageKey]);

  const handleColumnOrderChange = useCallback(
    (movableIds: string[]) => {
      const fullOrder =
        columnOrder.length > 0 ? columnOrder : allColumns.map((col) => col.id);
      setColumnOrder(applyMovableOrder(fullOrder, movableIds));
    },
    [allColumns, columnOrder],
  );

  const handleColumnSortChange = useCallback(
    (columnId: string, direction: false | "asc" | "desc") => {
      applyColumnSortDirection(table.getColumn(columnId), direction);
    },
    [table],
  );

  // Handle column drag start
  const handleColumnDragStart = useCallback(
    (columnId: string) => {
      if (!enableColumnReordering) return;

      // Check if column can be reordered
      const canReorder = columnConfig[columnId]?.canReorder ?? true;
      if (!canReorder) return;

      setDraggedColumn(columnId);
    },
    [enableColumnReordering, columnConfig],
  );

  // Handle column drag over
  const handleColumnDragOver = useCallback(
    (e: React.DragEvent, columnId: string) => {
      if (!enableColumnReordering || !draggedColumn) return;

      e.preventDefault();
      setDragOverColumn(columnId);
    },
    [enableColumnReordering, draggedColumn],
  );

  // Handle column drop
  const handleColumnDrop = useCallback(
    (targetColumnId: string) => {
      if (!enableColumnReordering || !draggedColumn) return;

      // Check if target column can be reordered
      const canReorder = columnConfig[targetColumnId]?.canReorder ?? true;
      if (!canReorder) {
        setDraggedColumn(null);
        setDragOverColumn(null);
        return;
      }

      // Reorder columns
      const newOrder = [...columnOrder];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(targetColumnId);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);
        setColumnOrder(newOrder);
      }

      setDraggedColumn(null);
      setDragOverColumn(null);
    },
    [enableColumnReordering, draggedColumn, columnOrder, columnConfig],
  );

  // Handle drag end
  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  }, []);

  // Get rows to display
  const rows = table.getRowModel().rows;

  // Pagination state
  const paginationState = table.state.pagination;
  const pageCount = table.getPageCount();

  // Determine states
  // Show loading skeleton when initially loading OR when fetching with no data yet
  const showLoadingSkeleton = isLoading || (isFetching && rows.length === 0);
  // Only show "no data" when not loading, not fetching, no error, and truly no data
  const showNoData = !isLoading && !isFetching && !error && rows.length === 0;

  // Get selected rows for row selection feature
  // Use rowSelection state as the dependency (not the `table` instance which changes every render).
  // Return a stable empty array reference when row selection is disabled to avoid
  // triggering the downstream useEffect on every render.
  const rowSelectionState = table.state.rowSelection;
  const selectedRows = useMemo(() => {
    if (!enableRowSelection) return EMPTY_SELECTED_ROWS;
    return table.getSelectedRowModel().rows.map((row) => row.original);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableRowSelection, rowSelectionState]);

  // Notify parent when row selection changes
  useEffect(() => {
    if (enableRowSelection && onRowSelectionChange) {
      onRowSelectionChange(selectedRows);
    }
  }, [enableRowSelection, onRowSelectionChange, selectedRows]);

  // Handle search changes
  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      onSearchChange?.(searchTerm);
    },
    [onSearchChange],
  );

  // Handle filter changes
  const handleFilterChange = useCallback(
    (
      newFilters: Record<
        string,
        string | string[] | { from: string; to: string }
      >,
    ) => {
      onFiltersChange?.(newFilters);
    },
    [onFiltersChange],
  );

  // Build available filters from config using SearchableSelect for select types
  const availableFilters = useMemo<AvailableFilter[]>(() => {
    if (!filterConfig?.filters) return [];

    return filterConfig.filters.map((filter) => {
      if (filter.type === "select" && filter.options) {
        const dropdownOptions: SearchableSelectOption[] = filter.options.map(
          (opt) => ({
            value: opt.value,
            label: opt.label,
          }),
        );

        if (filter.multiple) {
          const raw = filterValues[filter.id];
          const currentValues: string[] = Array.isArray(raw)
            ? raw
            : typeof raw === "string" && raw && raw !== "all"
              ? [raw]
              : [];

          return {
            id: filter.id,
            label: filter.label,
            component: (
              <SearchableSelect
                key={filter.id}
                value={currentValues}
                onChange={(vals: string[]) => {
                  handleFilterChange({
                    ...filterValues,
                    [filter.id]: vals,
                  });
                }}
                options={dropdownOptions}
                placeholder={filter.label}
                searchPlaceholder={`Search ${filter.label.toLowerCase()}…`}
                multiple
                aria-label={filter.label}
                showFieldPrefix
              />
            ),
          };
        }

        const currentValue = (filterValues[filter.id] as string) || "all";

        return {
          id: filter.id,
          label: filter.label,
          component: (
            <SearchableSelect
              key={filter.id}
              value={currentValue === "all" ? "" : currentValue}
              onChange={(val: string) => {
                handleFilterChange({
                  ...filterValues,
                  [filter.id]: val || "all",
                });
              }}
              options={dropdownOptions}
              placeholder={filter.label}
              searchPlaceholder={`Search ${filter.label.toLowerCase()}…`}
              aria-label={filter.label}
              showFieldPrefix
            />
          ),
        };
      }

      if (filter.type === "daterange") {
        const dateVal = normalizeDateRangeValue(filterValues[filter.id]);

        return {
          id: filter.id,
          label: filter.label,
          component: (
            <DateRangeDropdown
              key={filter.id}
              value={dateVal}
              onChange={(val: DateRangeValue) => {
                handleFilterChange({
                  ...filterValues,
                  [filter.id]: val,
                });
              }}
              placeholder={filter.label}
              aria-label={filter.label}
              showFieldPrefix
            />
          ),
        };
      }

      return {
        id: filter.id,
        label: filter.label,
        component: (
          <InlineFilters
            key={filter.id}
            filters={[filter]}
            values={filterValues}
            onChange={handleFilterChange}
          />
        ),
      };
    });
  }, [filterConfig, filterValues, handleFilterChange]);

  // Handle page changes
  const handlePageChange = useCallback(
    (page: number) => {
      // If we have a direct setter from the hook, use it instead of TanStack's internal setter
      if (onPageChangeDirect) {
        onPageChangeDirect(page);
      } else {
        // Fallback to TanStack's internal state management
        table.setPageIndex(page - 1);
      }
    },
    [table, onPageChangeDirect],
  );

  // Handle items per page changes
  const handleItemsPerPageChange = useCallback(
    (size: number) => {
      // If we have a direct setter from the hook, use it instead of TanStack's internal setter
      if (onItemsPerPageChangeDirect) {
        onItemsPerPageChangeDirect(size);
      } else {
        // Fallback to TanStack's internal state management
        table.setPageSize(size);
        table.setPageIndex(0);
      }
    },
    [table, onItemsPerPageChangeDirect],
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Enhanced Toolbar */}
      {(searchConfig?.enabled ||
        filterConfig?.enabled ||
        toolbarActions ||
        enableTableSettings) && (
        <EnhancedTableToolbar
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder={searchConfig?.placeholder}
          searchFields={searchConfig?.fields}
          recentSearches={recentSearches}
          onClearRecentSearches={onClearRecentSearches}
          availableFilters={availableFilters}
          enabledFilters={enabledFilterIds}
          appliedFilterIds={appliedFilterIdsOf(
            filterConfig?.filters,
            filterValues,
          )}
          onToggleFilterVisibility={handleToggleFilterVisibility}
          onSetAllFilterVisibility={handleSetAllFilterVisibility}
          showSearch={searchConfig?.enabled}
          showAdvancedFilters={
            showFilterSelector !== false &&
            filterConfig?.enabled &&
            filterConfig.filters.length > 0
          }
          selectedCount={enableRowSelection ? selectedRows.length : 0}
          onClearSelection={() => table.resetRowSelection()}
          bulkActions={bulkActions}
          showColumnSelector={enableTableSettings}
          columnSelectorColumns={tableColumns}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          enableColumnReordering={enableColumnReordering}
          onResetColumnOrder={handleResetColumnOrder}
          onColumnOrderChange={
            enableColumnReordering ? handleColumnOrderChange : undefined
          }
          onColumnSortChange={
            enableTableSettings ? handleColumnSortChange : undefined
          }
        >
          {/* Custom toolbar actions */}
          {toolbarActions}
        </EnhancedTableToolbar>
      )}

      {/* Content Area (Grid or Table) */}
      {viewMode === "grid" && renderGrid ? (
        <div className="py-4">
          {renderGrid(rows.map((row) => row.original))}
          {/* Grid Pagination */}
          {!isLoading && !error && totalCount > 0 && (
            <div className="mt-4 px-4 py-3 bg-white border border-gray-200 rounded-lg">
              <ModernPagination
                currentPage={paginationState.pageIndex + 1}
                totalPages={pageCount}
                totalItems={totalCount}
                itemsPerPage={paginationState.pageSize}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                loading={isLoading}
                showPageSizeSelector={true}
                pageSizeOptions={pageSizeOptions}
              />
            </div>
          )}
        </div>
      ) : (
        /* Table Container - includes both table and pagination in one bordered container */
        <div
          ref={containerRef}
          className="bg-white overflow-clip border border-gray-200 rounded-lg"
        >
          <div className="overflow-x-auto scrollbar-thin">
            <table
              className="w-full divide-y divide-gray-200"
              style={{
                tableLayout: "auto",
                minWidth: `${minTableWidth}px`,
              }}
            >
              {/* Table Header */}
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {/* Checkbox header column */}
                    {enableRowSelection && (
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-700 tracking-wider w-12 sticky left-0 z-10 bg-gray-50"
                        style={{ width: "48px", minWidth: "48px" }}
                      >
                        <TableCheckbox
                          checked={table.getIsAllPageRowsSelected()}
                          indeterminate={table.getIsSomePageRowsSelected()}
                          onChange={() => table.toggleAllPageRowsSelected()}
                          ariaLabel="Select all rows on current page"
                        />
                      </th>
                    )}
                    {headerGroup.headers.map((header, _headerIndex) => {
                      const column = header.column;
                      const canSort = column.getCanSort();
                      const sortDirection = column.getIsSorted();
                      const canReorder =
                        columnConfig[column.id]?.canReorder ?? true;
                      const isDragging = draggedColumn === column.id;
                      const isDragOver = dragOverColumn === column.id;

                      return (
                        <th
                          key={header.id}
                          className={cn(
                            "px-4 py-3 text-left text-xs font-semibold text-gray-700 tracking-wider",
                            "whitespace-normal",
                            enableColumnReordering &&
                              canReorder &&
                              "group/header transition-all duration-200",
                            isDragging && "opacity-50",
                            isDragOver &&
                              enableColumnReordering &&
                              canReorder &&
                              "border-l-2 border-primary",
                          )}
                          style={{
                            width: columnWidths[column.id],
                            minWidth:
                              header.getSize() !== 150 ? header.getSize() : 100,
                          }}
                          draggable={enableColumnReordering && canReorder}
                          onDragStart={() => handleColumnDragStart(column.id)}
                          onDragOver={(e) => handleColumnDragOver(e, column.id)}
                          onDrop={() => handleColumnDrop(column.id)}
                          onDragEnd={handleColumnDragEnd}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {enableColumnReordering && canReorder && (
                              <svg
                                className="h-4 w-4 text-gray-300 opacity-0 group-hover/header:opacity-100 transition-opacity cursor-grab active:cursor-grabbing flex-shrink-0"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                aria-label="Drag to reorder column"
                              >
                                <circle cx="5.5" cy="3" r="1.5" />
                                <circle cx="10.5" cy="3" r="1.5" />
                                <circle cx="5.5" cy="8" r="1.5" />
                                <circle cx="10.5" cy="8" r="1.5" />
                                <circle cx="5.5" cy="13" r="1.5" />
                                <circle cx="10.5" cy="13" r="1.5" />
                              </svg>
                            )}

                            {/* Header content with optional sorting */}
                            {canSort ? (
                              <button
                                type="button"
                                className="flex items-center gap-2 select-none hover:text-gray-900 transition-colors min-w-0"
                                onClick={() => column.toggleSorting()}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    column.toggleSorting();
                                  }
                                }}
                                aria-label={`Sort by ${header.isPlaceholder ? header.id : typeof header.column.columnDef.header === "string" ? header.column.columnDef.header : header.id}`}
                              >
                                <span>
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext(),
                                      )}
                                </span>

                                {/* Sort indicator */}
                                <div className="flex flex-col -space-y-1 flex-shrink-0">
                                  <ChevronUpIcon
                                    className={cn(
                                      "h-3 w-3 transition-colors",
                                      sortDirection === "asc"
                                        ? "text-primary"
                                        : "text-gray-400",
                                    )}
                                  />
                                  <ChevronDownIcon
                                    className={cn(
                                      "h-3 w-3 transition-colors",
                                      sortDirection === "desc"
                                        ? "text-primary"
                                        : "text-gray-400",
                                    )}
                                  />
                                </div>
                              </button>
                            ) : (
                              <span>
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              {/* Table Body */}
              <tbody className="bg-white divide-y divide-gray-200">
                {showLoadingSkeleton ? (
                  // Loading skeleton rows (shown on initial load OR when fetching with no data)
                  Array.from({ length: 10 }).map((_, index) => (
                    <tr key={`skeleton-row-${index}`} className="animate-pulse">
                      {/* Checkbox skeleton cell */}
                      {enableRowSelection && (
                        <td className="px-4 py-3 w-12 sticky left-0 z-10 bg-white">
                          <div className="h-4 w-4 bg-gray-200 rounded"></div>
                        </td>
                      )}
                      {columns.map((column, _colIndex) => (
                        <td
                          key={`${column.id}-skeleton-${index}`}
                          className="px-4 py-3"
                        >
                          <div className="h-4 bg-gray-200 rounded"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : error ? (
                  <tr>
                    <td
                      colSpan={columns.length + (enableRowSelection ? 1 : 0)}
                      className="px-4 py-12 text-center"
                    >
                      <div className="text-status-error-text font-medium mb-2">
                        Error loading data
                      </div>
                      <div className="text-gray-600 text-sm">
                        {error.message || "An unexpected error occurred"}
                      </div>
                    </td>
                  </tr>
                ) : showNoData ? (
                  <EmptyState
                    inTable
                    colSpan={columns.length + (enableRowSelection ? 1 : 0)}
                    variant={emptyVariant ?? "general"}
                    description={emptyMessage}
                  />
                ) : (
                  rows.map((row) => {
                    const isSelected =
                      enableRowSelection && row.getIsSelected();
                    const stickyBg = isSelected
                      ? "bg-blue-50 group-hover:bg-blue-100"
                      : "bg-white group-hover:bg-gray-50";

                    return (
                      <tr
                        key={row.id}
                        onClick={() => onRowClick?.(row.original)}
                        className={cn(
                          "group transition-colors hover:bg-gray-50",
                          onRowClick && "cursor-pointer",
                          rowClassName?.(row.original),
                          isSelected && "bg-blue-50 hover:bg-blue-100",
                        )}
                      >
                        {/* Checkbox cell -- clicks here toggle selection, not row navigation */}
                        {enableRowSelection && (
                          <td
                            className={cn(
                              "px-4 py-3 w-12 cursor-pointer sticky left-0 z-10",
                              stickyBg,
                            )}
                            style={{ width: "48px", minWidth: "48px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              row.toggleSelected();
                            }}
                          >
                            <TableCheckbox
                              checked={row.getIsSelected()}
                              onChange={() => row.toggleSelected()}
                              ariaLabel={`Select row ${row.id}`}
                            />
                          </td>
                        )}
                        {row.getVisibleCells().map((cell) => {
                          const cellMeta = cell.column.columnDef.meta as
                            ColumnMetaConfig | undefined;
                          return (
                            <td
                              key={cell.id}
                              className="px-4 py-3 text-sm text-gray-900"
                              style={{
                                width: columnWidths[cell.column.id],
                                minWidth:
                                  cell.column.getSize() !== 150
                                    ? cell.column.getSize()
                                    : 100,
                              }}
                            >
                              <div
                                className={cn(
                                  cellContentNeedsMinWidthZero(cellMeta) &&
                                    "min-w-0",
                                  getCellTextClass(cellMeta),
                                )}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - inside table container for seamless appearance */}
          {!isLoading && !error && totalCount > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50">
              <ModernPagination
                currentPage={paginationState.pageIndex + 1}
                totalPages={pageCount}
                totalItems={totalCount}
                itemsPerPage={paginationState.pageSize}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                loading={isLoading}
                showPageSizeSelector={true}
                pageSizeOptions={pageSizeOptions}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Export helper hook for row selection
export function useTableRowSelection<TData extends RowData>(
  table: TanStackTable<StockFeatures, TData>,
): {
  selectedRows: TData[];
  isAllSelected: boolean;
  isSomeSelected: boolean;
  toggleAllRows: () => void;
  toggleRow: (_rowId: string) => void;
} {
  const selectedRowIds = table.state.rowSelection;
  const rows = table.getRowModel().rows;

  const selectedRows = rows
    .filter((rowItem) => selectedRowIds[rowItem.id])
    .map((rowItem) => rowItem.original);

  const isAllSelected =
    rows.length > 0 && rows.every((rowItem) => selectedRowIds[rowItem.id]);
  const isSomeSelected =
    rows.some((rowItem) => selectedRowIds[rowItem.id]) && !isAllSelected;

  const toggleAllRows = () => {
    table.toggleAllRowsSelected(!isAllSelected);
  };

  const toggleRow = (_rowId: string) => {
    const rowItem = rows.find((r) => r.id === _rowId);
    if (rowItem) {
      rowItem.toggleSelected();
    }
  };

  return {
    selectedRows,
    isAllSelected,
    isSomeSelected,
    toggleAllRows,
    toggleRow,
  };
}
